<template>
  <div>
    <v-flex class="pb-16">
      <text-field
        v-model.trim="form.replyTo"
        :validator="vs.form.replyTo"
        :label="adminText('workflowsAll.reply_to_e_mail_a8e268a', 'Reply-to e-mail')"
      />
    </v-flex>
    <v-flex v-if="isPerformanceSelectingEnabled" class="pb-16">
      <v-select
        v-model="selectedEventId"
        :items="workflowUi(performanceDates)"
        :label="adminText('workflowsAll.performance_e4aee88', 'Performance*')"
        item-text="from"
        item-value="id"
        box
      />
    </v-flex>
    <v-flex class="pb-16">
      <select-field
        v-model="form.recipients"
        :validator="vs.form.recipients"
        :items="workflowUi(recipients)"
        :disabled="recipients.length === 1"
        :label="adminText('workflowsAll.recipients_faf993d', 'Recipients*')"
      />
    </v-flex>
    <v-flex v-if="ticketTypesLoading" class="pb-16">
      <vue-skeleton-loader rounded radius="4" width="100%" height="56px" animation="fade" />
    </v-flex>
    <v-flex v-else-if="ticketFilterEnabled && ticketTypes.length" class="pb-16">
      <v-autocomplete
        v-model="form.orderItemIds"
        :items="workflowUi(ticketTypeOptions)"
        item-value="id"
        item-text="label"
        :label="adminText('workflowsAll.type_of_ticket_3968f24', 'Type of ticket')"
        multiple
        small-chips
        deletable-chips
        box
      />
    </v-flex>
    <v-flex v-if="showSpecificRecipientSelect" class="pb-16">
      <vue-skeleton-loader
        v-if="isRecipientDataLoading"
        rounded
        radius="4"
        width="100%"
        height="56px"
        animation="fade"
      />
      <v-autocomplete
        v-else-if="form.recipients === 'Specific categories'"
        v-model="form.categories"
        :items="workflowUi(categories)"
        item-value="id"
        item-text="name"
        :label="adminText('workflowsAll.categories_6ccb600', 'Categories')"
        multiple
        small-chips
        deletable-chips
        box
        class="email-create-autocomplete"
        @focus="form.attendees = []"
      />
      <v-autocomplete
        v-else
        v-model="form.attendees"
        :items="workflowUi(filteredAttendees)"
        item-value="id"
        item-text="name"
        :label="adminText('workflowsAll.attendees_47d7867', 'Attendees*')"
        multiple
        small-chips
        deletable-chips
        box
        class="email-create-autocomplete"
        @focus="form.categories = []"
      />
    </v-flex>
    <v-flex class="pb-16">
      <text-field v-model.trim="form.subject" :validator="vs.form.subject" :label="adminText('workflowsAll.subject_8d183db', 'Subject')" />
    </v-flex>
    <v-flex class="pb-16">
      <text-editor-field
        v-model.trim="form.content"
        :validator="vs.form.content"
        :label="adminText('workflowsAll.content_4f9be05', 'Content')"
        height="390"
      />
    </v-flex>
  </div>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import {
  GET_EMAIL_RECIPIENT_PICKER_ACCREDITATIONS,
  GET_EVENT_CATEGORIES,
  GET_EVENTS_BY_EVENT_TEMPLATE_ID,
} from '@/graphql/event.graphql'
import moment from 'moment/moment'
import { GET_ORDER_ITEM_TICKET_OPTIONS } from '@/graphql/orderItem.graphql'

export default {
  mixins: [WorkflowDisplay],
  name: 'create-theater-event-email-form',
  props: {
    initialValues: {
      type: Object,
      default: () => {},
    },
    vs: {
      type: Object,
      default: () => {},
    },
    eventTemplateId: {
      default: null,
      type: String,
    },
    organizationSlug: String,
    initiallySelectedEventId: String,
    isPerformanceSelectingEnabled: {
      type: Boolean,
      default: false,
    },
    ticketFilterEnabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      form: [],
      recipients: ['All attendees', 'Specific categories', 'Specific attendees'],
      categories: [],
      attendees: [],
      initAttendees: [],
      initCategories: [],
      performanceDates: [],
      selectedEventId: null,
      ticketTypes: [],
      isRecipientDataLoading: false,
      ticketTypesLoadingCount: 0,
    }
  },
  computed: {
    showSpecificRecipientSelect() {
      return ['Specific categories', 'Specific attendees'].includes(this.form.recipients)
    },
    ticketTypesLoading() {
      return (
        this.ticketFilterEnabled &&
        !!this.selectedEventId &&
        (this.isRecipientDataLoading || this.ticketTypesLoadingCount > 0)
      )
    },
    selectedOrderItemIds() {
      return (this.form.orderItemIds || []).map(orderItemId => orderItemId.toString())
    },
    ticketTypeOptions() {
      return this.ticketTypes.map(ticketType => {
        const recipientCount = this.getTicketTypeRecipientCount(ticketType.id)

        return {
          ...ticketType,
          label: `${ticketType.name} (${recipientCount})`,
        }
      })
    },
    filteredAttendees() {
      if (!this.selectedOrderItemIds.length) {
        return this.attendees
      }

      return this.attendees.filter(attendee => {
        const attendeeOrderItemIds = (attendee.orderItemIds || []).map(orderItemId => orderItemId.toString())

        return attendeeOrderItemIds.some(orderItemId => this.selectedOrderItemIds.includes(orderItemId))
      })
    },
  },
  watch: {
    form: {
      handler(val) {
        if (val.type === 'Automated' && this.recipients.length > 1) {
          if (this.recipients.indexOf('Specific attendees') >= 0) {
            this.recipients.splice(this.recipients.indexOf('Specific attendees'), 1)
          }
        }
        this.$emit('values', val)
      },
      deep: true,
    },
    'form.orderItemIds'() {
      this.removeAttendeesWithoutSelectedTicket()
    },
    selectedEventId: {
      async handler(val, previousEventId) {
        if (previousEventId && previousEventId !== val) {
          this.form.orderItemIds = []
        }
        this.recipients = ['All attendees', 'Specific categories', 'Specific attendees']
        this.isRecipientDataLoading = true

        try {
          const {
            data: {
              getEventCategories: { categories },
            },
          } = await this.$apollo.query({
            query: GET_EVENT_CATEGORIES,
            variables: { id: val },
          })

          this.categories = categories
          if (this.categories.length) {
            this.categories.map(categorie => {
              if (this.initCategories.includes(categorie.id.toString())) {
                this.form.categories.push(categorie)
              }
            })
          } else {
            if (this.recipients.indexOf('Specific attendees') >= 0) {
              this.recipients.splice(this.recipients.indexOf('Specific categories'), 1)
            }
          }

          const {
            data: {
              getEmailRecipientPickerAccreditations: { attendees },
            },
          } = await this.$apollo.query({
            query: GET_EMAIL_RECIPIENT_PICKER_ACCREDITATIONS,
            variables: {
              eventId: val,
            },
          })

          this.attendees = attendees.map(function (attendee) {
            return {
              name:
                (attendee.fields['First name'] || attendee.fields['Ime'] || '') +
                ' ' +
                (attendee.fields['Last name'] || attendee.fields['Prezime'] || ''),
              ...attendee,
            }
          })
          if (this.initAttendees.length) {
            this.attendees.map(attendee => {
              if (this.initAttendees.includes(attendee.id.toString())) {
                this.form.attendees.push(attendee)
              }
            })
          }
          if (!this.attendees.length && this.recipients.length > 1) {
            if (this.recipients.indexOf('Specific attendees') >= 0) {
              this.recipients.splice(this.recipients.indexOf('Specific attendees'), 1)
            }
          }
        } finally {
          this.isRecipientDataLoading = false
        }
      },
    },
  },
  mounted() {
    const { attendees, categories, meta, ...rest } = this.initialValues
    this.form = {
      ...rest,
      categories: [],
      attendees: [],
      orderItemIds: meta && meta.orderItemIds ? meta.orderItemIds : [],
    }
    this.initAttendees = attendees || []
    this.initCategories = categories || []
  },
  methods: {
    removeAttendeesWithoutSelectedTicket() {
      if (!this.selectedOrderItemIds.length || !this.form.attendees || !this.form.attendees.length) {
        return
      }

      const filteredAttendeeIds = this.filteredAttendees.map(attendee => attendee.id.toString())
      this.form.attendees = this.form.attendees.filter(attendee => {
        const attendeeId = attendee && typeof attendee === 'object' ? attendee.id : attendee

        return filteredAttendeeIds.includes(attendeeId.toString())
      })
    },
    categoriesChanged(categories) {
      this.form.categories = categories.map(categorie => categorie.id)
      this.form.attendees = []
    },
    attendeesChanged(attendees) {
      this.form.attendees = attendees.map(attendee => attendee.id)
      this.form.categories = []
    },
    getTicketTypeRecipientCount(orderItemId) {
      const normalizedOrderItemId = orderItemId.toString()

      return this.attendees.filter(attendee =>
        (attendee.orderItemIds || [])
          .map(attendeeOrderItemId => attendeeOrderItemId.toString())
          .includes(normalizedOrderItemId),
      ).length
    },
  },
  apollo: {
    getOrderItems: {
      query: GET_ORDER_ITEM_TICKET_OPTIONS,
      loadingKey: 'ticketTypesLoadingCount',
      skip() {
        return !this.ticketFilterEnabled || !this.selectedEventId
      },
      variables() {
        return {
          eventId: this.selectedEventId,
          pagination: { page: 1, per_page: -1 },
        }
      },
      result({ data, loading }) {
        if (!loading) {
          const {
            getOrderItems: { items },
          } = data
          this.ticketTypes = items.filter(item => !item.type || item.type === 'ticket')
        }
      },
    },
    getEventsByEventTemplateId: {
      query: GET_EVENTS_BY_EVENT_TEMPLATE_ID,
      variables() {
        return {
          organizationSlug: this.organizationSlug,
          eventTemplateId: this.eventTemplateId,
        }
      },
      result({ data: { getEventsByEventTemplateId } }) {
        this.performanceDates = [
          ...getEventsByEventTemplateId.map(p => {
            return { ...p, from: moment(p.from).format('DD.MM.YYYY u HH:mm') }
          }),
        ]
        this.selectedEventId = this.initiallySelectedEventId || getEventsByEventTemplateId[0].id
      },
    },
  },
}
</script>

<style>
.email-create-autocomplete.v-text-field--box .v-input__slot {
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  min-height: 56px;
}

.email-create-autocomplete.v-text-field--box .v-input__slot::before,
.email-create-autocomplete.v-text-field--box .v-input__slot::after {
  display: none;
}

.email-create-autocomplete .v-select__slot,
.email-create-autocomplete .v-select__selections {
  min-height: 56px;
}

.email-create-autocomplete .v-label {
  color: #666666;
  font-size: 16px;
  letter-spacing: 0.5px;
}

.email-create-autocomplete.v-input--is-focused .v-input__slot {
  border-color: #0277bd;
}
</style>
